import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { BarChart3, Bell, Check, ChevronDown, CircleHelp, Copy, CreditCard, FileText, Home, KeyRound, Link2, LogOut, Menu, Megaphone, Plus, Settings, ShieldCheck, Sparkles, Target, Trash2, User, Users, X } from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../auth/AuthProvider";
import { clearWhopAccessToken, WhopAdsSurface } from "../ads/WhopAdsSurface";
import { BillingWorkspace } from "../ads/BillingWorkspace";
import { DraftCampaignLauncher } from "../ads/DraftCampaignLauncher";
import { api, cacheScope, IMPERSONATION_KEY, readCache, removeCache, writeCache } from "../lib/api";
import { MetaAssetsView } from "./MetaAssetsView";
import { AdminConsole } from "../admin/AdminConsole";

type View = "overview" | "campaigns" | "tracking" | "settings" | "admin";
type SettingsSection = "general" | "billing" | "identity" | "team" | "developer" | "profile";
type WorkspaceAccess = { role: "owner" | "admin" | "member"; permissions: string[] };
type SetupStatus = {
  ready: boolean;
  blocked: boolean;
  missing: Array<"billing" | "identity">;
  billing_ready: boolean;
  identity_ready: boolean;
  identity_count: number;
  card_added: boolean;
  can_manage_billing: boolean;
};
type AdAccountSummary = { id: string; name: string; status?: string; selected: boolean; shared?: boolean; created_at: string };
type AccountsResponse = { active_account_id: string | null; waitlist?: boolean; accounts: AdAccountSummary[]; access?: WorkspaceAccess | null; platform_admin?: boolean; features?: { custom_pixel?: boolean } };
type TopbarMenu = "help" | "notifications" | "profile";

const navItems: Array<[View, string, typeof Home]> = [
  ["overview", "Today", Home],
  ["campaigns", "Campaigns", Megaphone],
  ["tracking", "Pixel & websites", Target],
  ["settings", "Settings", Settings],
];

function routeState(path: string): { view: View; settings: SettingsSection } {
  const parts = path.split("/").filter(Boolean);
  const slug = parts[1] || "overview";
  if (slug === "billing") return { view: "settings", settings: "billing" };
  if (slug === "assets") return { view: "settings", settings: "identity" };
  if (slug === "reporting") return { view: "campaigns", settings: "general" };
  if (slug === "admin") return { view: "admin", settings: "general" };
  const view = navItems.some(([candidate]) => candidate === slug) ? slug as View : "overview";
  const settings = view === "settings" && (parts[2] === "billing" || parts[2] === "identity" || parts[2] === "team" || parts[2] === "developer" || parts[2] === "profile") ? parts[2] : "general";
  return { view, settings };
}

function initials(value: string) {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "OT";
}

export function DashboardPage() {
  const { session, booting, user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const preview = import.meta.env.DEV && new URLSearchParams(location.search).get("preview") === "1";
  const [impersonating] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("imp");
    if (token) {
      sessionStorage.setItem(IMPERSONATION_KEY, token);
      window.history.replaceState(null, "", window.location.pathname);
    }
    return Boolean(token || sessionStorage.getItem(IMPERSONATION_KEY));
  });
  const exitImpersonation = () => {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    window.location.href = "/app/admin";
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [creatorError, setCreatorError] = useState("");
  const [campaignEpoch, setCampaignEpoch] = useState(0);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [accountQuery, setAccountQuery] = useState("");
  const [accountSortDesc, setAccountSortDesc] = useState(false);
  const [topbarMenu, setTopbarMenu] = useState<TopbarMenu | null>(null);
  const topbarActionsRef = useRef<HTMLDivElement>(null);
  const [accountName, setAccountName] = useState("");
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [accountEpoch, setAccountEpoch] = useState(0);
  const [accounts, setAccounts] = useState<AdAccountSummary[]>(preview ? [{ id: "preview", name: "Northstar Commerce", selected: true, created_at: "" }] : []);
  const [waitlist, setWaitlist] = useState(false);
  const [accountNotice, setAccountNotice] = useState("");
  // A pending account can be opened read-only: the view is just its
  // waiting-for-approval state, never the ad tooling.
  const [viewingPendingId, setViewingPendingId] = useState<string | null>(null);
  const [access, setAccess] = useState<WorkspaceAccess | null>(preview ? { role: "owner", permissions: [] } : null);
  const [platformAdmin, setPlatformAdmin] = useState(false);
  const [customPixel, setCustomPixel] = useState(false);
  const [setup, setSetup] = useState<SetupStatus | null | undefined>(preview ? {
    ready: false,
    blocked: true,
    missing: ["billing", "identity"],
    billing_ready: false,
    identity_ready: false,
    identity_count: 0,
    card_added: false,
    can_manage_billing: true,
  } : undefined);
  const { view, settings } = routeState(location.pathname);

  const loadAccounts = useCallback(async () => {
    if (preview) return;
    const response = await api<AccountsResponse>("/api/ads/accounts");
    setAccounts(response.accounts);
    setAccess(response.access || { role: "owner", permissions: [] });
  }, [preview]);
  const setupCacheKey = !preview && cacheScope(user?.id) ? `setup:${cacheScope(user?.id)}:active` : null;
  const visibleAccounts = useMemo(() => {
    const needle = accountQuery.trim().toLowerCase();
    const matched = needle ? accounts.filter((account) => account.name.toLowerCase().includes(needle)) : accounts;
    return [...matched].sort((a, b) => a.name.localeCompare(b.name) * (accountSortDesc ? -1 : 1));
  }, [accounts, accountQuery, accountSortDesc]);
  const refreshSetup = useCallback(async () => {
    if (preview) return null;
    try {
      const next = await api<SetupStatus>("/api/ads/setup-status");
      setSetup(next);
      if (setupCacheKey) writeCache(setupCacheKey, next);
      return next;
    }
    catch { setSetup(null); return null; }
  }, [preview, setupCacheKey]);

  useEffect(() => {
    if (!session || preview) return;
    let active = true;
    // Paint the last-known setup state immediately; the live fetch replaces it.
    if (setupCacheKey) {
      const cached = readCache<SetupStatus>(setupCacheKey);
      if (cached) setSetup((current) => (current === undefined ? cached : current));
    }
    void (async () => {
      try {
        const accountResponse = await api<AccountsResponse>("/api/ads/accounts");
        if (!active) return;
        setAccounts(accountResponse.accounts);
        setAccess(accountResponse.access || { role: "owner", permissions: [] });
        setPlatformAdmin(Boolean(accountResponse.platform_admin));
        setCustomPixel(Boolean(accountResponse.features?.custom_pixel));
        // Waitlisted accounts have no usable ad account yet, so there is no
        // setup state to load; the portal renders the waitlist screen instead.
        if (accountResponse.waitlist) {
          setWaitlist(true);
          setSetup(undefined);
          return;
        }
        setWaitlist(false);
        const setupResponse = await api<SetupStatus>("/api/ads/setup-status");
        if (!active) return;
        setSetup(setupResponse);
        if (setupCacheKey) writeCache(setupCacheKey, setupResponse);
      } catch {
        if (active) setSetup(null);
      }
    })();
    return () => { active = false; };
  }, [session, preview, accountEpoch, setupCacheKey]);

  useEffect(() => () => clearWhopAccessToken(), []);

  useEffect(() => {
    if (!topbarMenu) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!topbarActionsRef.current?.contains(event.target as Node)) setTopbarMenu(null);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTopbarMenu(null);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [topbarMenu]);

  useEffect(() => {
    const legacy: Record<string, string> = {
      "/app/billing": "/app/settings/billing",
      "/app/assets": "/app/settings/identity",
      "/app/reporting": "/app/campaigns",
      "/app/settings/admin": "/app/admin",
    };
    if (legacy[location.pathname]) navigate(legacy[location.pathname], { replace: true });
  }, [location.pathname, navigate]);

  const setView = (nextView: View) => {
    setViewingPendingId(null);
    navigate(nextView === "overview" ? "/app" : `/app/${nextView}`);
    setSidebarOpen(false);
    setTopbarMenu(null);
  };
  const openSettings = (section: SettingsSection) => {
    navigate(section === "general" ? "/app/settings" : `/app/settings/${section}`);
    setSidebarOpen(false);
    setTopbarMenu(null);
  };
  const toggleTopbarMenu = (menu: TopbarMenu) => setTopbarMenu((current) => current === menu ? null : menu);
  const handleSignOut = () => {
    setTopbarMenu(null);
    clearWhopAccessToken();
    void signOut();
  };
  // Cached numbers belong to the previously selected account; drop them the
  // moment the active account changes so no other account's data flashes in.
  const clearActiveAccountCache = () => {
    const scope = cacheScope(user?.id);
    if (!scope) return;
    removeCache(`setup:${scope}:active`);
    removeCache(`overview:${scope}:active`);
  };
  const activateAccount = async (id: string) => {
    if (accounts.find((account) => account.id === id)?.selected) { setAccountMenuOpen(false); return; }
    setAccountBusy(true);
    setAccountError("");
    try {
      const switched = await api<{ impersonation_token?: string }>("/api/ads/accounts/select", { method: "POST", body: JSON.stringify({ account_id: id }) });
      if (switched.impersonation_token) sessionStorage.setItem(IMPERSONATION_KEY, switched.impersonation_token);
      clearWhopAccessToken();
      clearActiveAccountCache();
      setAccountMenuOpen(false);
      setCreatorOpen(false);
      setSetup(undefined);
      setAccountEpoch((value) => value + 1);
    } catch (caught) {
      setAccountError(caught instanceof Error ? caught.message : "The ad account could not be selected.");
    } finally {
      setAccountBusy(false);
    }
  };
  const createAccount = async () => {
    const name = accountName.trim();
    if (name.length < 2) { setAccountError("Enter an ad account name."); return; }
    setAccountBusy(true);
    setAccountError("");
    try {
      await api("/api/ads/accounts", { method: "POST", body: JSON.stringify({ name }) });
      // New ad accounts are requests: Oxwell provisions, sets up billing, and
      // approves before the account becomes usable. Nothing switches yet.
      setAccountName("");
      setAccountNotice(`“${name}” was requested. The Oxwell team will set it up and approve it.`);
      await loadAccounts();
    } catch (caught) {
      setAccountError(caught instanceof Error ? caught.message : "The ad account could not be created.");
    } finally {
      setAccountBusy(false);
    }
  };

  if (booting) return <main className="callback-page"><Logo /><div className="loading-orbit" /><h1>Preparing your workspace…</h1></main>;
  if (!session) return <Navigate to="/login" replace />;

  const email = user?.email || "client@example.com";
  const fallbackName = String(user?.user_metadata?.business_name || email.split("@")[0] || "Ads portal client");
  const activeAccount = accounts.find((account) => account.selected) || accounts.find((account) => (account.status || "approved") === "approved");
  const viewingPending = accounts.find((account) => account.id === viewingPendingId && (account.status || "approved") !== "approved") || null;

  if (waitlist && !impersonating) {
    return <main className="waitlist-page">
      <div className="waitlist-card">
        <Logo />
        <span className="eyebrow">ACCOUNT REVIEW</span>
        <h1>You’re on the waitlist</h1>
        <p>The Oxwell team is reviewing your account. Provisioning, billing, and setup are all handled for you — we’ll email <strong>{email}</strong> as soon as your ad account is approved.</p>
        {accounts.length > 0 && <ul className="waitlist-requests">{accounts.map((account) => <li key={account.id}><span>{account.name}</span><em>{account.status === "provisioned" ? "Being set up" : "Pending review"}</em></li>)}</ul>}
        <div className="waitlist-actions">
          <a className="button button--secondary" href="mailto:hello@oxwell.io">Contact the team</a>
          {platformAdmin && <button className="button button--secondary" type="button" onClick={() => navigate("/admin")}>Open admin console</button>}
          <button className="button button--primary" type="button" onClick={handleSignOut}>Sign out</button>
        </div>
      </div>
    </main>;
  }
  const activeName = activeAccount?.name || fallbackName;
  const canCreate = setup?.ready === true;
  const refreshCampaigns = () => {
    setCreatorError("");
    setCampaignEpoch((value) => value + 1);
  };

  return <>
    {impersonating && <div className="impersonation-banner" role="status"><ShieldCheck /> Managing client ad account <strong>{activeName}</strong> as administrator. Actions here are real. <button type="button" onClick={exitImpersonation}>Exit to admin</button></div>}
    <main className="dashboard-shell">
    {sidebarOpen && <button className="sidebar-backdrop" type="button" aria-label="Close navigation" onClick={() => setSidebarOpen(false)} />}
    <aside className={`dashboard-sidebar ${sidebarOpen ? "is-open" : ""}`}>
      <div className="sidebar-logo"><a className="rail-brand" href="/app" aria-label="Oxwell home"><span className="rail-brand__ox">OX</span><span className="rail-brand__well">WELL</span></a><button type="button" onClick={() => setSidebarOpen(false)} aria-label="Close navigation"><X /></button></div>
      <nav>{navItems.map(([key, label, Icon]) => <button className={view === key ? "is-active" : ""} key={key} type="button" onClick={() => setView(key)}><Icon /><span>{label}</span></button>)}{platformAdmin && <button className={view === "admin" ? "is-active" : ""} type="button" onClick={() => setView("admin")}><ShieldCheck /><span>Admin</span></button>}</nav>
      <a className="rail-help" href="mailto:hello@oxwell.io" aria-label="Contact the team" title="Contact the team"><CircleHelp /></a>
      <button className="signout-button" type="button" onClick={handleSignOut} aria-label="Sign out" title="Sign out"><LogOut /></button>
    </aside>

    <section className="dashboard-main">
      <header className="dashboard-topbar">
        <button className="mobile-menu" type="button" onClick={() => { setSidebarOpen(true); setTopbarMenu(null); }} aria-label="Open navigation"><Menu /></button>
        <div className="workspace-switcher-wrap">
          <button className={`workspace-switcher ${accountMenuOpen ? "is-open" : ""}`} type="button" aria-expanded={accountMenuOpen} onClick={() => { setAccountQuery(""); setAccountMenuOpen((value) => !value); }}>
            <span className="workspace-avatar">{initials(viewingPending?.name || activeName)}</span><span className="workspace-switcher__copy"><strong>{viewingPending?.name || activeName}</strong><small>{viewingPending ? "Pending Oxwell approval" : "Ad account"}</small></span><ChevronDown />
          </button>
          {accountMenuOpen && <div className="workspace-menu">
            <div className="workspace-menu__heading"><strong>Ad accounts</strong><span>Select the account you want to manage.</span></div>
            {accounts.length > 5 && <div className="workspace-menu__tools">
              <input value={accountQuery} placeholder="Search ad accounts" aria-label="Search ad accounts" onChange={(event) => setAccountQuery(event.target.value)} />
              <button type="button" aria-label={accountSortDesc ? "Sorted Z to A, switch to A to Z" : "Sorted A to Z, switch to Z to A"} onClick={() => setAccountSortDesc((value) => !value)}>{accountSortDesc ? "Z–A" : "A–Z"}</button>
            </div>}
            <div className="workspace-menu__list">{visibleAccounts.map((account) => {
              const pending = (account.status || "approved") !== "approved";
              const viewing = pending ? viewingPendingId === account.id : account.selected && !viewingPendingId;
              return <button className={viewing ? "is-selected" : ""} type="button" key={account.id} onClick={() => { if (pending) { setViewingPendingId(account.id); setAccountMenuOpen(false); } else { setViewingPendingId(null); void activateAccount(account.id); } }} disabled={accountBusy}><span>{initials(account.name)}</span><div><strong>{account.name}</strong><small>{pending ? "Pending Oxwell approval" : account.selected ? "Current account" : account.shared ? "Shared with you" : "Switch account"}</small></div>{viewing && <Check />}</button>;
            })}
            {visibleAccounts.length === 0 && <p className="workspace-menu__empty">No ad accounts match that search.</p>}</div>
            <div className="workspace-menu__create"><label htmlFor="new-ad-account">Request a new ad account</label><div><input id="new-ad-account" value={accountName} maxLength={80} placeholder="Account name" onChange={(event) => setAccountName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void createAccount(); }} /><button type="button" onClick={() => void createAccount()} disabled={accountBusy || accountName.trim().length < 2}><Plus />{accountBusy ? "Requesting…" : "Request"}</button></div>{accountError && <p role="alert">{accountError}</p>}{accountNotice && <p className="workspace-menu__notice">{accountNotice}</p>}</div>
          </div>}
        </div>
                <div className="topbar-actions" ref={topbarActionsRef}>
          <div className="topbar-action-wrap topbar-action-wrap--help">
            <button className={`topbar-icon-button ${topbarMenu === "help" ? "is-active" : ""}`} type="button" aria-label="Help and support" aria-haspopup="dialog" aria-expanded={topbarMenu === "help"} onClick={() => toggleTopbarMenu("help")}><CircleHelp /></button>
            {topbarMenu === "help" && <section className="topbar-popover" role="dialog" aria-label="Help and support">
              <div className="topbar-popover__heading"><span><CircleHelp /></span><div><strong>How can we help?</strong><small>Get help with setup, campaigns, or your account.</small></div></div>
              <div className="topbar-popover__actions"><a href="mailto:hello@oxwell.io?subject=Oxwell%20support" onClick={() => setTopbarMenu(null)}>Email platform support</a><button type="button" onClick={() => openSettings("general")}>Open account settings</button></div>
            </section>}
          </div>
          <div className="topbar-action-wrap topbar-action-wrap--notifications">
            <button className={`topbar-icon-button ${topbarMenu === "notifications" ? "is-active" : ""}`} type="button" aria-label="Notifications" aria-haspopup="dialog" aria-expanded={topbarMenu === "notifications"} onClick={() => toggleTopbarMenu("notifications")}><Bell />{Boolean(setup?.missing.length) && <i aria-hidden="true" />}</button>
            {topbarMenu === "notifications" && <section className="topbar-popover topbar-popover--notifications" role="dialog" aria-label="Notifications">
              <div className="topbar-popover__title"><strong>Notifications</strong>{Boolean(setup?.missing.length) && <span>{setup?.missing.length} to do</span>}</div>
              {setup === undefined && <div className="topbar-empty"><span className="topbar-empty__icon"><Bell /></span><strong>Checking your account</strong><small>Your latest setup status is loading.</small></div>}
              {setup === null && <div className="topbar-empty"><span className="topbar-empty__icon"><CircleHelp /></span><strong>Status unavailable</strong><small>We couldn’t load your account status.</small><button type="button" onClick={() => void refreshSetup()}>Try again</button></div>}
              {setup?.ready && <div className="topbar-empty"><span className="topbar-empty__icon topbar-empty__icon--success"><Check /></span><strong>You’re all caught up</strong><small>There are no account actions waiting for you.</small></div>}
              {setup && !setup.ready && <div className="topbar-notification-list">
                {!setup.billing_ready && (impersonating
                  ? <button type="button" onClick={() => openSettings("billing")}><span><CreditCard /></span><div><strong>Connect billing</strong><small>Add the payment method for this ad account.</small></div><ChevronDown /></button>
                  : <button type="button" disabled><span><CreditCard /></span><div><strong>Billing setup in progress</strong><small>Handled by the Oxwell team — no action needed.</small></div></button>)}
                {!setup.identity_ready && <button type="button" disabled={!setup.billing_ready} onClick={() => openSettings("identity")}><span><Link2 /></span><div><strong>Connect an identity</strong><small>{setup.billing_ready ? "Choose the Facebook or Instagram identity for campaigns." : "Available after billing is connected."}</small></div><ChevronDown /></button>}
              </div>}
            </section>}
          </div>
          <div className="topbar-action-wrap topbar-action-wrap--profile">
            <button className={`topbar-avatar ${topbarMenu === "profile" ? "is-active" : ""}`} type="button" aria-label="Open profile menu" aria-haspopup="dialog" aria-expanded={topbarMenu === "profile"} onClick={() => toggleTopbarMenu("profile")}>{initials(activeName)}</button>
            {topbarMenu === "profile" && <section className="topbar-popover topbar-popover--profile" role="dialog" aria-label="Profile menu">
              <div className="topbar-profile"><span>{initials(email)}</span><div><strong>{email}</strong><small>{activeName} · {access?.role || "owner"}</small></div></div>
              <div className="topbar-popover__actions"><button type="button" onClick={() => openSettings("profile")}><User />View profile</button><button type="button" onClick={() => openSettings("general")}><Settings />Account settings</button><button className="is-danger" type="button" onClick={handleSignOut}><LogOut />Sign out</button></div>
            </section>}
          </div>
        </div>
      </header>
      {viewingPending
        ? <div className="dashboard-page"><div className="dashboard-heading"><div><span className="eyebrow">OXWELL</span><h1>{viewingPending.name}</h1><p>This ad account is waiting for Oxwell.</p></div></div>
          <div className="waitlist-card waitlist-card--inline">
            <span className="eyebrow">ACCOUNT REVIEW</span>
            <h1>{viewingPending.status === "provisioned" ? "Being set up by Oxwell" : "Pending review"}</h1>
            <p>The Oxwell team provisions this ad account, sets up its billing, and approves it — no action needed from you. We’ll email <strong>{email}</strong> when it’s ready.</p>
            <div className="waitlist-actions">
              <a className="button button--secondary" href="mailto:hello@oxwell.io">Contact the team</a>
              {activeAccount && <button className="button button--primary" type="button" onClick={() => setViewingPendingId(null)}>Back to {activeAccount.name}</button>}
            </div>
          </div></div>
        : <DashboardView key={`${activeAccount?.id || "loading"}-${accountEpoch}`} view={view} settings={settings} accountName={activeName} setup={setup} impersonating={impersonating} overviewCacheKey={!preview && cacheScope(user?.id) ? `overview:${cacheScope(user?.id)}:active` : null} access={access} platformAdmin={platformAdmin} customPixel={customPixel} preview={preview} campaignEpoch={campaignEpoch} creatorError={creatorError} accounts={accounts} accountBusy={accountBusy} onSwitchAccount={activateAccount} onAccountsChanged={() => setAccountEpoch((value) => value + 1)} onCreate={() => { if (canCreate) { setCreatorError(""); setCreatorOpen(true); } }} onCampaignLaunched={refreshCampaigns} onSettings={openSettings} onSetupChanged={refreshSetup} />}
    </section>
    {creatorOpen && canCreate && <WhopAdsSurface surface="creator" onClose={() => { setCreatorOpen(false); refreshCampaigns(); }} onLaunched={refreshCampaigns} onError={setCreatorError} />}
  </main>
  </>;
}

function DashboardView({ view, settings, accountName, setup, impersonating, overviewCacheKey, access, platformAdmin, customPixel, preview, campaignEpoch, creatorError, accounts, accountBusy, onSwitchAccount, onAccountsChanged, onCreate, onCampaignLaunched, onSettings, onSetupChanged }: {
  view: View;
  settings: SettingsSection;
  accountName: string;
  setup: SetupStatus | null | undefined;
  impersonating: boolean;
  overviewCacheKey: string | null;
  access: WorkspaceAccess | null;
  platformAdmin: boolean;
  customPixel: boolean;
  preview: boolean;
  campaignEpoch: number;
  creatorError: string;
  accounts: AdAccountSummary[];
  accountBusy: boolean;
  onSwitchAccount: (id: string) => void;
  onAccountsChanged: () => void;
  onCreate: () => void;
  onCampaignLaunched: () => void;
  onSettings: (section: SettingsSection) => void;
  onSetupChanged: () => Promise<SetupStatus | null>;
}) {
  const title = useMemo(() => {
    if (view === "settings" && settings === "billing") return ["Billing", "Choose the payment method used for campaign spend."];
    if (view === "settings" && settings === "identity") return ["Identity", "Connect the Facebook Page or Instagram account your campaigns run from."];
    return ({
      overview: [`Welcome, ${accountName}`, "Set up and manage this ad account."],
      campaigns: ["Campaigns", "Create campaigns and review performance in one view."],
      tracking: ["Pixel & websites", "Set up the pixel and confirm your websites."],
      settings: ["Settings", "Manage this ad account and its connections."],
      admin: ["Platform admin", "Spend, profit, retention, and every client workspace."],
    }[view]);
  }, [view, settings, accountName]);
  const campaignReady = setup?.ready === true;
  const allowed = (permission: string) => !access || access.role === "owner" || access.role === "admin" || access.permissions.includes(permission);

  return <div className={`dashboard-page dashboard-page--${view}`}>
    <div className="dashboard-heading"><div><span className="eyebrow">OXWELL</span><h1>{title[0]}</h1><p>{title[1]}</p></div>{view === "campaigns" && campaignReady && <button className="button button--primary" type="button" onClick={onCreate}><Plus /> Create campaign</button>}</div>
    {view === "campaigns" && !allowed("campaigns") && <section className="setup-gate"><CircleHelp /><strong>You don’t have campaign access</strong><p>Ask the account owner or an admin to grant you campaign access.</p></section>}
    {view === "tracking" && !allowed("tracking") && <section className="setup-gate"><CircleHelp /><strong>You don’t have tracking access</strong><p>Ask the account owner or an admin to grant you tracking access.</p></section>}
    {view === "campaigns" && allowed("campaigns") && creatorError && <div className="campaign-creator-error" role="alert"><CircleHelp /><div><strong>Campaign creator needs attention</strong><span>{creatorError}</span><button className="button button--secondary button--small" type="button" onClick={onCreate}>Open campaign creator</button></div></div>}
    {(view === "overview" || view === "campaigns") && !preview && campaignReady && allowed("campaigns") && <PaymentFailures />}
    {view === "overview" && <Overview setup={setup} impersonating={impersonating} cacheKey={overviewCacheKey} onCreate={onCreate} onSettings={onSettings} portfolio={!preview && accounts.filter((account) => !account.shared).length > 1 ? <PortfolioCard onSwitch={onSwitchAccount} busy={accountBusy} /> : null} />}
    {view === "campaigns" && allowed("campaigns") && setup === undefined && <section className="setup-gate"><CircleHelp /><strong>Checking account setup</strong><p>Billing and identity are verified before campaign tools open.</p></section>}
    {view === "campaigns" && allowed("campaigns") && setup === null && <section className="setup-gate"><CircleHelp /><strong>We couldn’t check your setup</strong><p>Refresh the page or contact support if this continues.</p></section>}
    {view === "campaigns" && allowed("campaigns") && setup && !setup.ready && <section className="setup-gate"><Megaphone /><strong>Finish setup before creating campaigns</strong><p>{setup.billing_ready ? "Connect an identity. Campaign creation and performance reporting open together when both are ready." : impersonating ? "Add billing, then connect an identity. Campaign creation and performance reporting open together when both are ready." : "The Oxwell team is completing your billing setup. Identity connection opens as soon as it’s done."}</p>{(setup.billing_ready || impersonating) && <button className="button button--primary" type="button" onClick={() => onSettings(setup.billing_ready ? "identity" : "billing")}>{setup.billing_ready ? "Set up identity" : "Set up billing"}</button>}</section>}
    {view === "campaigns" && allowed("campaigns") && campaignReady && !preview && <DraftCampaignLauncher revision={campaignEpoch} onLaunched={onCampaignLaunched} onBilling={() => onSettings("billing")} onEdit={onCreate} />}
    {view === "campaigns" && allowed("campaigns") && campaignReady && <WhopAdsSurface key={campaignEpoch} surface="campaigns" />}
    {view === "tracking" && allowed("tracking") && setup === undefined && <section className="setup-gate"><CircleHelp /><strong>Checking account setup</strong><p>Billing and identity are verified before pixel tools open.</p></section>}
    {view === "tracking" && allowed("tracking") && setup === null && <section className="setup-gate"><CircleHelp /><strong>We couldn’t check your setup</strong><p>Refresh the page or contact support if this continues.</p></section>}
    {view === "tracking" && allowed("tracking") && setup && !setup.ready && <section className="setup-gate"><Target /><strong>Finish setup before configuring your pixel</strong><p>{setup.billing_ready ? "Connect an identity. Pixel and website tools open when both are ready." : impersonating ? "Add billing, then connect an identity. Pixel and website tools open when both are ready." : "The Oxwell team is completing your billing setup. Pixel tools open as soon as identity is connected."}</p>{(setup.billing_ready || impersonating) && <button className="button button--primary" type="button" onClick={() => onSettings(setup.billing_ready ? "identity" : "billing")}>{setup.billing_ready ? "Set up identity" : "Set up billing"}</button>}</section>}
    {view === "tracking" && allowed("tracking") && campaignReady && <WhopAdsSurface surface="tracking" customPixel={customPixel} />}
    {view === "settings" && <SettingsView section={settings} accountName={accountName} setup={setup} impersonating={impersonating} access={access} platformAdmin={platformAdmin} preview={preview} onSection={onSettings} onSetupChanged={onSetupChanged} onAccountRenamed={onAccountsChanged} />}
    {view === "admin" && platformAdmin && !preview && <AdminConsole />}
  </div>;
}

type OverviewData = {
  from: string;
  to: string;
  daily: Array<{ date: string; spend: number }>;
  spend: number;
  impressions: number;
  clicks: number;
  results: number;
  has_campaign: boolean;
  pixel_active: boolean;
};

const formatMoney = (value: number) => `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatCount = (value: number) => value.toLocaleString("en-US");

function SpendChart({ daily }: { daily: Array<{ date: string; spend: number }> }) {
  const plotWidth = 860;
  const plotHeight = 220;
  const left = 64;
  const bottom = 46;
  const top = 16;
  const max = Math.max(...daily.map((day) => day.spend), 1);
  const barWidth = plotWidth / Math.max(daily.length, 1);
  const compact = (value: number) => value >= 1000 ? `$${(value / 1000).toFixed(1)}k` : value >= 10 ? `$${Math.round(value)}` : `$${value.toFixed(2)}`;
  const ticks = [0, 0.5, 1].map((fraction) => max * fraction);
  return <svg className="admin-chart" viewBox={`0 0 ${left + plotWidth + 16} ${top + plotHeight + bottom}`} role="img" aria-label="Daily spend for the last 30 days">
    {ticks.map((tick) => {
      const y = top + plotHeight - (tick / max) * plotHeight;
      return <g key={tick}>
        <line x1={left} x2={left + plotWidth} y1={y} y2={y} className="admin-chart__grid" />
        <text x={left - 8} y={y + 4} className="admin-chart__tick" textAnchor="end">{compact(tick)}</text>
      </g>;
    })}
    <line x1={left} x2={left + plotWidth} y1={top + plotHeight} y2={top + plotHeight} className="admin-chart__axis" />
    {daily.map((day, index) => {
      const barHeight = day.spend > 0 ? Math.max(3, (day.spend / max) * plotHeight) : 0;
      const x = left + index * barWidth + 2;
      const width = Math.max(barWidth - 4, 2);
      const barTop = top + plotHeight - barHeight;
      const labelX = x + width / 2;
      return <g key={day.date}>
        {day.spend > 0 && <rect x={x} y={barTop} width={width} height={barHeight} rx={3} className="admin-chart__bar"><title>{`${day.date}: ${formatMoney(day.spend)}`}</title></rect>}
        {day.spend > 0 && <text x={labelX} y={Math.max(barTop - 6, top + 10)} className="admin-chart__value" textAnchor="middle">{compact(day.spend)}</text>}
        <text x={labelX} y={top + plotHeight + 12} className="admin-chart__label" textAnchor="end" transform={`rotate(-45 ${labelX} ${top + plotHeight + 12})`}>{day.date.slice(5)}</text>
      </g>;
    })}
  </svg>;
}

type PaymentFailure = { campaign_id: string; title: string; updated_at: string | null; reason: string };

// Paused campaigns whose ad-spend charge failed. Billing on this portal is
// handled by the platform team, so the client sees the state and who is on it,
// and the retry lives in the admin console.
function PaymentFailures() {
  const [data, setData] = useState<{ failures: PaymentFailure[] } | null>(null);
  useEffect(() => {
    let cancelled = false;
    api<{ failures: PaymentFailure[] }>("/api/ads/payment-failures").then((payload) => { if (!cancelled) setData(payload); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!data || data.failures.length === 0) return null;
  return <section className="payment-failures" role="alert">
    <div className="payment-failures__head"><CreditCard /><div><strong>{data.failures.length === 1 ? "A campaign is paused: its ad-spend charge failed" : `${data.failures.length} campaigns are paused: their ad-spend charges failed`}</strong><span>Ads stop delivering until the charge succeeds. The Oxwell team has been notified and is handling the payment. No action is needed from you.</span></div></div>
    <ul>{data.failures.map((failure) => <li key={failure.campaign_id}><div><strong>{failure.title}</strong><span>{failure.reason}</span></div><small>Handled by the Oxwell team</small></li>)}</ul>
  </section>;
}

type DeveloperData = {
  business_id: string;
  base_url: string;
  keys: Array<{ id: string; name: string; prefix: string; created_at: string; last_used_at: string | null; expires_at: string | null }>;
};

// Self-serve API access: connection facts up top, then key management.
// Everything else (endpoints, parameters, examples) lives in the API docs.
function DeveloperView() {
  const [data, setData] = useState<DeveloperData | null | undefined>(undefined);
  const [name, setName] = useState("");
  const [expiryDays, setExpiryDays] = useState("0");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [freshKey, setFreshKey] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState("");
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<DeveloperData>("/api/ads/developer")
      .then((payload) => { if (!cancelled) setData(payload); })
      .catch((caught) => { if (!cancelled) { setData(null); setError(caught instanceof Error ? caught.message : "Developer settings could not be loaded."); } });
    return () => { cancelled = true; };
  }, [epoch]);

  const copy = (label: string, value: string) => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      window.setTimeout(() => setCopied(""), 1600);
    }).catch(() => setError("Copy failed. Select the value and copy it manually."));
  };

  const createKey = async () => {
    setBusy("create");
    setError("");
    try {
      const created = await api<{ name: string; key: string }>("/api/ads/developer/keys", { method: "POST", body: JSON.stringify({ name: name.trim(), expires_in_days: Number(expiryDays) }) });
      setFreshKey({ name: created.name, key: created.key });
      setName("");
      setExpiryDays("0");
      setEpoch((value) => value + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The API key could not be created.");
    } finally {
      setBusy("");
    }
  };

  const revoke = async (keyId: string, keyName: string) => {
    if (!window.confirm(`Revoke ${keyName}? Requests using it stop working immediately.`)) return;
    setBusy(keyId);
    setError("");
    try {
      await api(`/api/ads/developer/keys/${encodeURIComponent(keyId)}`, { method: "DELETE" });
      setEpoch((value) => value + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The API key could not be revoked.");
    } finally {
      setBusy("");
    }
  };

  if (data === undefined) return <section className="settings-card"><div className="settings-row"><div><strong>Loading developer settings…</strong></div></div></section>;
  if (data === null) return <section className="settings-card"><div className="settings-row"><div><strong>Developer settings unavailable</strong><span>{error || "Try again shortly."}</span></div><button className="button button--secondary button--small" type="button" onClick={() => { setError(""); setData(undefined); setEpoch((value) => value + 1); }}>Try again</button></div></section>;

  const formatWhen = (value: string | null) => value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "never";

  return <>
    <section className="settings-card dev-card">
      <div className="dev-intro">
        <div className="dev-intro__copy">
          <span className="dev-intro__icon"><KeyRound /></span>
          <div>
            <strong>Oxwell Ads API</strong>
            <p>Create campaigns, upload creatives, launch ads, and read reporting over HTTPS. Every request a key makes is scoped to this ad account.</p>
          </div>
        </div>
        <a className="button button--primary" href="/api-docs" target="_blank" rel="noreferrer">Open the API reference</a>
      </div>
      <div className="dev-facts">
        <div className="dev-fact">
          <small>Ad account ID</small>
          <span className="dev-fact__row"><code>{data.business_id}</code><button type="button" aria-label="Copy ad account ID" onClick={() => copy("biz", data.business_id)}>{copied === "biz" ? <Check /> : <Copy />}</button></span>
        </div>
        <div className="dev-fact">
          <small>Base URL</small>
          <span className="dev-fact__row"><code>{data.base_url}</code><button type="button" aria-label="Copy base URL" onClick={() => copy("base", data.base_url)}>{copied === "base" ? <Check /> : <Copy />}</button></span>
        </div>
        <div className="dev-fact">
          <small>Authentication</small>
          <span className="dev-fact__row"><code>Authorization: Bearer oxk_live_…</code></span>
        </div>
      </div>
    </section>

    <section className="settings-card dev-card">
      <div className="dev-intro">
        <div className="dev-intro__copy">
          <span className="dev-intro__icon dev-intro__icon--count">{data.keys.length}</span>
          <div>
            <strong>API keys</strong>
            <p>Up to 5 active keys. The full secret is shown once, at creation, and stored only as a hash.</p>
          </div>
        </div>
      </div>
      <div className="dev-create">
        <label className="dev-create__field dev-create__field--name">
          <small>Key name</small>
          <input value={name} maxLength={60} placeholder="e.g. Reporting pipeline" aria-label="New key name" onChange={(event) => { setName(event.target.value); setError(""); }} onKeyDown={(event) => { if (event.key === "Enter" && name.trim().length >= 2) void createKey(); }} />
        </label>
        <label className="dev-create__field">
          <small>Expiry</small>
          <select className="dev-expiry" aria-label="Key expiry" value={expiryDays} onChange={(event) => setExpiryDays(event.target.value)}>
            <option value="0">No expiry</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="365">1 year</option>
          </select>
        </label>
        <button className="button button--primary" type="button" disabled={busy === "create" || name.trim().length < 2} onClick={() => void createKey()}>{busy === "create" ? "Creating…" : "Create key"}</button>
      </div>
      {error && <div className="form-alert" role="alert" style={{ margin: "0 24px 16px" }}>{error}</div>}
      {freshKey && <div className="dev-key-reveal" role="status">
        <strong>“{freshKey.name}” is ready. Copy it now: it won’t be shown again.</strong>
        <span className="dev-fact__row"><code>{freshKey.key}</code><button type="button" aria-label="Copy the new API key" onClick={() => copy("fresh", freshKey.key)}>{copied === "fresh" ? <Check /> : <Copy />}</button></span>
        <button className="button button--secondary button--small" type="button" onClick={() => setFreshKey(null)}>I saved it</button>
      </div>}
      {data.keys.length === 0 && !freshKey
        ? <div className="dev-empty"><p>No keys yet. Name one above and it is created instantly; the secret appears here once, right after creation.</p></div>
        : data.keys.length > 0 && <table className="dev-keys">
          <thead><tr><th>Name</th><th>Key</th><th>Created</th><th>Last used</th><th>Expires</th><th /></tr></thead>
          <tbody>
            {data.keys.map((key) => <tr key={key.id}>
              <td><strong>{key.name}</strong></td>
              <td><code>{key.prefix}…</code></td>
              <td>{formatWhen(key.created_at)}</td>
              <td>{formatWhen(key.last_used_at)}</td>
              <td>{key.expires_at ? <span className={new Date(key.expires_at).getTime() - Date.now() < 7 * 864e5 ? "dev-expiry-soon" : ""}>{formatWhen(key.expires_at)}</span> : "never"}</td>
              <td><button className="button button--secondary button--small" type="button" disabled={busy === key.id} onClick={() => void revoke(key.id, key.name)}>{busy === key.id ? "Revoking…" : "Revoke"}</button></td>
            </tr>)}
          </tbody>
        </table>}
    </section>
  </>;
}

function Overview({ setup, impersonating, cacheKey, onCreate, onSettings, portfolio }: { setup: SetupStatus | null | undefined; impersonating: boolean; cacheKey: string | null; onCreate: () => void; onSettings: (section: SettingsSection) => void; portfolio?: ReactNode }) {
  // Start from the last-known numbers so a reload never flashes zeros; the
  // live response replaces them (and the cache) as soon as it lands.
  const [overview, setOverview] = useState<OverviewData | null>(() => (cacheKey ? readCache<OverviewData>(cacheKey) : null));
  useEffect(() => {
    let cancelled = false;
    api<OverviewData>("/api/ads/overview").then((payload) => {
      if (cancelled) return;
      setOverview(payload);
      if (cacheKey) writeCache(cacheKey, payload);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [cacheKey]);
  const stats = [
    ["Spend", overview ? formatMoney(overview.spend) : "$0.00", overview && overview.spend > 0 ? "Last 30 days" : "No activity yet"],
    ["Impressions", overview ? formatCount(overview.impressions) : "0", overview && overview.impressions > 0 ? "Last 30 days" : "Ready for launch"],
    ["Clicks", overview ? formatCount(overview.clicks) : "0", overview && overview.clicks > 0 ? "Last 30 days" : "Awaiting campaigns"],
    ["Results", overview ? formatCount(overview.results) : "0", overview && overview.results > 0 ? "Last 30 days" : "Start your first campaign"],
  ];
  const hasSpendData = Boolean(overview && overview.daily.some((day) => day.spend > 0));
  return <>
    {setup && !setup.ready && <SetupChecklist setup={setup} impersonating={impersonating} onSettings={onSettings} />}
    {setup?.ready && overview && !overview.has_campaign && <section className="next-action"><Sparkles /><div><span>Recommended</span><strong>Create your first campaign</strong><p>Choose a goal, audience, budget, and creative.</p></div><button className="button button--primary" type="button" onClick={onCreate}>Create campaign</button></section>}
    <section className="stat-grid">{stats.map(([label, value, note]) => <article key={label}><span>{label}</span><strong>{value}</strong><small>{note}</small></article>)}</section>
    {portfolio}
    <section className="overview-grid">
      <article className="chart-card"><div className="card-heading"><div><strong>Performance</strong><span>Last 30 days</span></div></div>{hasSpendData && overview
        ? <SpendChart daily={overview.daily} />
        : <div className="empty-chart"><BarChart3 /><strong>No campaign data yet</strong><span>Your performance chart appears after a campaign starts running.</span>{setup?.ready && <button className="button button--secondary button--small" type="button" onClick={onCreate}>Create campaign</button>}</div>}</article>
      <article className="launch-card">{(() => {
        const done = [setup?.billing_ready, setup?.identity_ready, overview?.has_campaign, overview?.pixel_active].filter(Boolean).length;
        return <>
          <div className="launch-card__head"><strong>Finish account setup</strong><span>{done} of 4</span></div>
          <div className="launch-card__progress" role="progressbar" aria-valuemin={0} aria-valuemax={4} aria-valuenow={done}><i style={{ width: `${(done / 4) * 100}%` }} /></div>
          <ol><li className={setup?.billing_ready ? "is-done" : ""}><CheckDot />Billing connected</li><li className={setup?.identity_ready ? "is-done" : ""}><CheckDot />Identity connected</li><li className={overview?.has_campaign ? "is-done" : ""}><CheckDot />Create your first campaign</li><li className={overview?.pixel_active ? "is-done" : ""}><CheckDot />Set up your pixel</li></ol>
        </>;
      })()}</article>
    </section>
    <section className="campaign-empty"><div><FileText /><div><strong>Recent campaigns</strong><span>Your newest campaigns will appear here.</span></div></div>{setup?.ready && <button type="button" onClick={onCreate}><Plus /> New campaign</button>}</section>
  </>;
}

type PortfolioData = {
  from: string;
  to: string;
  total_spend: number;
  today_spend: number;
  accounts: Array<{ id: string; name: string; spend: number; today_spend: number; selected: boolean }>;
};

// Cross-account rollup for owners with several ad accounts: one clear spend
// window at a time (last 30 days or today), each account ranked by it, with a
// one-click switch into any account.
function PortfolioCard({ onSwitch, busy }: { onSwitch: (id: string) => void; busy: boolean }) {
  const [data, setData] = useState<PortfolioData | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [window30, setWindow30] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api<PortfolioData>("/api/ads/portfolio").then((payload) => { if (!cancelled) setData(payload); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!data || data.accounts.length < 2) return null;
  const spendOf = (account: PortfolioData["accounts"][number]) => window30 ? account.spend : account.today_spend;
  const ranked = [...data.accounts].sort((a, b) => spendOf(b) - spendOf(a) || a.name.localeCompare(b.name));
  const max = Math.max(...ranked.map(spendOf), 0.01);
  const visible = showAll ? ranked : ranked.slice(0, 8);
  const hidden = ranked.length - visible.length;
  const rangeLabel = window30 ? `${formatDay(data.from)} – ${formatDay(data.to)}` : `Since midnight, ${formatDay(data.to)}`;
  return <section className="portfolio-card">
    <div className="card-heading">
      <div>
        <strong>All ad accounts</strong>
        <span>Spend across your {data.accounts.length} ad accounts · {rangeLabel}</span>
        <div className="portfolio-toggle" role="group" aria-label="Spend window">
          <button type="button" className={window30 ? "is-active" : ""} onClick={() => setWindow30(true)}>Last 30 days</button>
          <button type="button" className={window30 ? "" : "is-active"} onClick={() => setWindow30(false)}>Today</button>
        </div>
      </div>
      <div className="portfolio-total">
        <strong>{formatMoney(window30 ? data.total_spend : data.today_spend)}</strong>
        <small>{window30 ? `${formatMoney(data.today_spend)} of it today` : `${formatMoney(data.total_spend)} in the last 30 days`}</small>
      </div>
    </div>
    <ol className="portfolio-list">
      {visible.map((account, index) => <li key={account.id}>
        <span className="portfolio-rank">{index + 1}</span>
        <div className="portfolio-row">
          <div className="portfolio-row__head"><strong>{account.name}</strong><span>{formatMoney(spendOf(account))}</span></div>
          <div className="portfolio-bar"><i style={{ width: `${Math.max(spendOf(account) > 0 ? 2 : 0, (spendOf(account) / max) * 100)}%` }} /></div>
        </div>
        {account.selected
          ? <span className="portfolio-current"><Check /> Current</span>
          : <button className="button button--secondary button--small" type="button" disabled={busy} onClick={() => onSwitch(account.id)}>View</button>}
      </li>)}
    </ol>
    {hidden > 0 && <button className="portfolio-more" type="button" onClick={() => setShowAll(true)}>Show all {ranked.length} accounts</button>}
    {(window30 ? data.total_spend : data.today_spend) === 0 && <p className="portfolio-note">{window30 ? "No spend recorded in the last 30 days across these accounts." : "No spend recorded yet today across these accounts."}</p>}
  </section>;
}

function formatDay(value: string): string {
  const parsed = Date.parse(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

function SetupChecklist({ setup, impersonating, onSettings }: { setup: SetupStatus; impersonating: boolean; onSettings: (section: SettingsSection) => void }) {
  const steps = [
    { number: "01", title: "Billing", description: impersonating ? "Add the payment card that pays for campaigns on this account." : "The Oxwell team adds and manages the payment card that funds this account.", done: setup.billing_ready, open: true, section: "billing" as SettingsSection, cta: "Set up billing", selfServe: impersonating },
    { number: "02", title: "Identity", description: "Connect an existing Facebook Page or create a Facebook Page powered by your platform.", done: setup.identity_ready, open: setup.billing_ready, section: "identity" as SettingsSection, cta: "Set up identity", selfServe: true },
  ];
  return <section className="onboarding-card">
    <div className="onboarding-card__header"><span><Sparkles /></span><div><span className="eyebrow">ACCOUNT SETUP</span><h2>Two steps before your first campaign</h2><p>{impersonating ? "Add billing first, then choose the identity your campaigns run from." : "Oxwell handles billing; you choose the identity your campaigns run from."}</p></div></div>
    <ol>{steps.map((step) => <li className={`${step.done ? "is-complete" : ""} ${!step.open ? "is-locked" : ""}`} key={step.number}><span className="onboarding-step-number">{step.done ? <Check /> : step.number}</span><div><strong>{step.title}</strong><p>{step.description}</p></div>{step.done ? <em>Done</em> : !step.selfServe ? <em>Handled by Oxwell</em> : step.open ? <button className="button button--primary button--small" type="button" onClick={() => onSettings(step.section)}>{step.cta}</button> : <em>After billing</em>}</li>)}</ol>
    <p className="onboarding-card__footnote"><strong>Campaigns stay closed until both steps are complete.</strong> Each ad account has its own billing, identity, campaigns, reporting, and pixel setup.</p>
  </section>;
}

function CheckDot() { return <span className="check-dot"><span /></span>; }

function SettingsView({ section, accountName, setup, impersonating, access, platformAdmin, preview, onSection, onSetupChanged, onAccountRenamed }: { section: SettingsSection; accountName: string; setup: SetupStatus | null | undefined; impersonating: boolean; access: WorkspaceAccess | null; platformAdmin: boolean; preview: boolean; onSection: (section: SettingsSection) => void; onSetupChanged: () => Promise<SetupStatus | null>; onAccountRenamed: () => void }) {
  const [nameDraft, setNameDraft] = useState(accountName);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameNote, setNameNote] = useState("");
  const isOwner = !access || access.role === "owner";
  const saveName = async () => {
    const next = nameDraft.trim().replace(/\s+/g, " ");
    if (next.length < 2 || next === accountName) return;
    setNameBusy(true);
    setNameNote("");
    try {
      await api("/api/ads/account-name", { method: "POST", body: JSON.stringify({ name: next }) });
      setNameNote("Saved.");
      onAccountRenamed();
    } catch (caught) {
      setNameNote(caught instanceof Error ? caught.message : "The account could not be renamed.");
    } finally {
      setNameBusy(false);
    }
  };
  const canManageTeam = !access || access.role === "owner" || access.role === "admin";
  const allowedBilling = impersonating && (!access || access.role !== "member" || access.permissions.includes("billing"));
  // The scheduling timezone is a campaign preference, not billing: owners and
  // admins may set it in their own sessions, matching the worker's guard.
  const allowedTimezone = !access || access.role !== "member" || access.permissions.includes("billing");
  const allowedIdentity = !access || access.role !== "member" || access.permissions.includes("identity");
  return <div className="settings-workspace">
    <nav className="settings-tabs" aria-label="Settings sections">
      <button className={section === "general" ? "is-active" : ""} type="button" onClick={() => onSection("general")}><Settings />General</button>
      {impersonating && <button className={section === "billing" ? "is-active" : ""} type="button" onClick={() => onSection("billing")}><CreditCard />Billing</button>}
      <button className={section === "identity" ? "is-active" : ""} type="button" onClick={() => onSection("identity")}><Link2 />Identity</button>
      {canManageTeam && <button className={section === "team" ? "is-active" : ""} type="button" onClick={() => onSection("team")}><Users />Team</button>}
      {(!access || access.role !== "member") && <button className={section === "developer" ? "is-active" : ""} type="button" onClick={() => onSection("developer")}><KeyRound />Developer</button>}
      <button className={section === "profile" ? "is-active" : ""} type="button" onClick={() => onSection("profile")}><User />Profile</button>
    </nav>
    {section === "general" && <section className="settings-card"><div className="settings-card__head"><span>{initials(accountName)}</span><div><strong>{accountName}</strong><small>Managed ad account</small></div></div><div className="settings-row"><div><strong>Account name</strong><span>{isOwner ? "Shown in your account selector. A Facebook Page created after a rename uses the new name; an existing Page keeps its name." : "The name shown in your account selector."}{nameNote && <em className="settings-note"> {nameNote}</em>}</span></div>{isOwner && !preview ? <span className="settings-row__controls"><input value={nameDraft} maxLength={80} aria-label="Account name" onChange={(event) => { setNameDraft(event.target.value); setNameNote(""); }} /><button className="button button--primary button--small" type="button" disabled={nameBusy || nameDraft.trim().replace(/\s+/g, " ").length < 2 || nameDraft.trim().replace(/\s+/g, " ") === accountName} onClick={() => void saveName()}>{nameBusy ? "Saving…" : "Save"}</button></span> : <input value={accountName} readOnly />}</div><div className="settings-row"><div><strong>Support</strong><span>Account and campaign help from our team.</span></div><a className="button button--secondary button--small" href="mailto:hello@oxwell.io">Contact support</a></div>{!preview && <TimezoneRow allowed={allowedTimezone} />}</section>}
    {section === "billing" && !preview && !allowedBilling && <section className="setup-gate"><CreditCard /><strong>Billing is managed by Oxwell</strong><p>The Oxwell team adds and manages the payment method for this ad account. Contact them for any billing question.</p><a className="button button--secondary button--small" href="mailto:hello@oxwell.io">Contact the team</a></section>}
    {section === "billing" && !preview && allowedBilling && <BillingWorkspace onChanged={onSetupChanged} />}
    {section === "billing" && preview && <section className="setup-gate"><CreditCard /><strong>Hosted billing setup</strong><p>The live account mounts its secure, account-scoped billing setup here.</p></section>}
    {section === "identity" && !allowedIdentity && <section className="setup-gate"><Link2 /><strong>You don’t have identity access</strong><p>Ask the account owner or an admin to grant you identity access.</p></section>}
    {section === "identity" && allowedIdentity && setup?.billing_ready && <MetaAssetsView onChanged={() => { void onSetupChanged(); }} />}
    {section === "identity" && allowedIdentity && !setup?.billing_ready && <section className="setup-gate"><CreditCard /><strong>Finish billing first</strong><p>{impersonating ? "Each ad account needs its own payment method before an identity can be connected or created." : "The Oxwell team is completing billing for this ad account. Identity connection opens right after."}</p>{impersonating && <button className="button button--primary" type="button" onClick={() => onSection("billing")}>Set up billing</button>}</section>}
    {section === "team" && canManageTeam && !preview && <TeamView role={access?.role || "owner"} />}
    {section === "developer" && (!access || access.role !== "member") && !preview && <DeveloperView />}
    {section === "developer" && access && access.role === "member" && <section className="setup-gate"><KeyRound /><strong>You don’t have developer access</strong><p>Ask the ad account owner or an admin to manage API keys.</p></section>}
    {section === "profile" && <ProfileView accountName={accountName} access={access} platformAdmin={platformAdmin} />}
  </div>;
}

// Campaign start/end times and daily reporting buckets follow this zone.
// Stored on the Whop account preferences; owners and billing members edit it.
// Zone entries are built once at module load: every IANA zone labeled and
// searchable by its current GMT offset (DST means offsets are as of today).
type ZoneEntry = { id: string; label: string; search: string; offset: number };

function zoneOffsetMinutes(zone: string, at: Date): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longOffset" }).formatToParts(at);
    const raw = parts.find((part) => part.type === "timeZoneName")?.value || "GMT";
    const match = raw.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!match) return 0;
    return (match[1] === "-" ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]));
  } catch { return 0; }
}

function buildZoneEntries(): ZoneEntry[] {
  let ids: string[];
  try { ids = Intl.supportedValuesOf("timeZone"); } catch { ids = ["America/New_York", "Europe/London", "Asia/Dubai", "UTC"]; }
  const now = new Date();
  return ids.map((id) => {
    const offset = zoneOffsetMinutes(id, now);
    const sign = offset < 0 ? "-" : "+";
    const absolute = Math.abs(offset);
    const gmt = `GMT${sign}${String(Math.floor(absolute / 60)).padStart(2, "0")}:${String(absolute % 60).padStart(2, "0")}`;
    const shortGmt = `gmt${sign}${Math.floor(absolute / 60)}${absolute % 60 ? `:${String(absolute % 60).padStart(2, "0")}` : ""}`;
    return {
      id,
      offset,
      label: `(${gmt}) ${id.replaceAll("_", " ")}`,
      search: `${id.toLowerCase().replaceAll("_", " ")} ${gmt.toLowerCase()} ${shortGmt} utc${shortGmt.slice(3)}`,
    };
  }).sort((a, b) => a.offset - b.offset || a.id.localeCompare(b.id));
}

const ZONE_ENTRIES = buildZoneEntries();

function TimezoneRow({ allowed }: { allowed: boolean }) {
  const browserZone = useMemo(() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "America/New_York"; } }, []);
  const [current, setCurrent] = useState<string | null | undefined>(undefined);
  const [selected, setSelected] = useState("");
  const [zoneQuery, setZoneQuery] = useState("");
  const [zoneOpen, setZoneOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const comboRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  useEffect(() => {
    let cancelled = false;
    api<{ timezone: string | null }>("/api/ads/preferences")
      .then((data) => { if (!cancelled) { setCurrent(data.timezone); setSelected(data.timezone || browserZone); } })
      .catch(() => { if (!cancelled) setCurrent(null); });
    return () => { cancelled = true; };
  }, [browserZone]);
  useEffect(() => {
    if (!zoneOpen) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!comboRef.current?.contains(event.target as Node)) setZoneOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [zoneOpen]);
  const visibleZones = useMemo(() => {
    const needle = zoneQuery.trim().toLowerCase().replace(/(gmt|utc)\s+/g, "$1");
    return needle ? ZONE_ENTRIES.filter((zone) => zone.search.includes(needle)) : ZONE_ENTRIES;
  }, [zoneQuery]);
  const selectedEntry = ZONE_ENTRIES.find((zone) => zone.id === selected);
  const choose = (zoneId: string) => {
    setSelected(zoneId);
    setZoneQuery("");
    setZoneOpen(false);
  };
  const openList = () => {
    setZoneQuery("");
    setZoneOpen(true);
    const startAt = Math.max(0, ZONE_ENTRIES.findIndex((zone) => zone.id === selected));
    setHighlight(startAt);
    queueMicrotask(() => document.getElementById(`tz-option-${startAt}`)?.scrollIntoView({ block: "center" }));
  };
  const onComboKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") { setZoneOpen(false); return; }
    if (!zoneOpen && (event.key === "ArrowDown" || event.key === "Enter")) { event.preventDefault(); openList(); return; }
    if (!zoneOpen) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      const next = Math.min(Math.max(highlight + delta, 0), visibleZones.length - 1);
      setHighlight(next);
      document.getElementById(`tz-option-${next}`)?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (visibleZones[highlight]) choose(visibleZones[highlight].id);
    }
  };
  const save = async () => {
    setBusy(true);
    setNote("");
    try {
      const result = await api<{ timezone: string }>("/api/ads/preferences", { method: "POST", body: JSON.stringify({ timezone: selected }) });
      setCurrent(result.timezone);
      setNote("Saved.");
    } catch (caught) {
      setNote(caught instanceof Error ? caught.message : "The timezone could not be saved.");
    } finally {
      setBusy(false);
    }
  };
  return <div className="settings-row">
    <div><strong>Scheduling timezone</strong><span>{current === undefined ? "Loading your current timezone…" : "Campaign start and end times and daily reporting use this timezone."}{note && <em className="settings-note"> {note}</em>}</span></div>
    <span className="settings-row__controls">
      <div className="tz-combo" ref={comboRef}>
        <input
          role="combobox"
          aria-expanded={zoneOpen}
          aria-controls="tz-combo-list"
          aria-autocomplete="list"
          aria-label="Scheduling timezone"
          placeholder={selectedEntry?.label || "Choose a timezone"}
          value={zoneOpen ? zoneQuery : selectedEntry?.label || ""}
          disabled={!allowed || current === undefined}
          onFocus={openList}
          onClick={() => { if (!zoneOpen) openList(); }}
          onChange={(event) => { setZoneQuery(event.target.value); setHighlight(0); if (!zoneOpen) setZoneOpen(true); }}
          onKeyDown={onComboKeyDown}
        />
        <ChevronDown aria-hidden="true" />
        {zoneOpen && <ul className="tz-combo__list" id="tz-combo-list" role="listbox" aria-label="Timezones">
          {visibleZones.map((zone, index) => <li
            key={zone.id}
            id={`tz-option-${index}`}
            role="option"
            aria-selected={zone.id === selected}
            className={index === highlight ? "is-active" : ""}
            onMouseDown={(event) => { event.preventDefault(); choose(zone.id); }}
            onMouseEnter={() => setHighlight(index)}
          >{zone.label}</li>)}
          {visibleZones.length === 0 && <li className="tz-combo__empty" aria-disabled="true">No timezones match. Try a city name or an offset like GMT-3.</li>}
        </ul>}
      </div>
      <button className="button button--primary button--small" type="button" disabled={!allowed || busy || current === undefined || selected === current} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
    </span>
  </div>;
}

function ProfileView({ accountName, access, platformAdmin }: { accountName: string; access: WorkspaceAccess | null; platformAdmin: boolean }) {
  const { user, signOut } = useAuth();
  const email = user?.email || "";
  const role = access?.role || "owner";
  const roleCopy = role === "owner"
    ? "You own this workspace and hold every permission."
    : role === "admin"
      ? "You manage this workspace with full access."
      : "You have the permissions the account owner assigned to you.";
  return <section className="settings-card">
    <div className="settings-card__head"><span>{initials(email)}</span><div><strong>{email || "Signed in"}</strong><small>Oxwell profile</small></div></div>
    <div className="settings-row"><div><strong>Signed in as</strong><span>The email that receives your sign-in codes and workspace invitations.</span></div><span className="profile-value">{email || "—"}</span></div>
    <div className="settings-row"><div><strong>Current workspace</strong><span>The ad account you are working in right now.</span></div><span className="profile-value">{accountName}</span></div>
    <div className="settings-row"><div><strong>Workspace role</strong><span>{roleCopy}</span></div><span className={`profile-role profile-role--${role}`}>{role}</span></div>
    {platformAdmin && <div className="settings-row"><div><strong>Platform access</strong><span>You can open the platform admin dashboard.</span></div><a className="admin-builtin" href="/admin" target="_blank" rel="noreferrer"><ShieldCheck /> Platform admin</a></div>}
    <div className="settings-row"><div><strong>Session</strong><span>Sign out of this browser when you are done, especially on shared devices.</span></div><button className="button button--secondary button--small" type="button" onClick={() => { clearWhopAccessToken(); void signOut(); }}><LogOut /> Sign out</button></div>
  </section>;
}

type TeamMember = { id: string; email: string; role: "admin" | "member"; permissions: string[]; status: "invited" | "active"; created_at: string };
type TeamResponse = { role: string; available_permissions: string[]; members: TeamMember[] };

const PERMISSION_META: Record<string, { label: string; hint: string }> = {
  campaigns: { label: "Campaigns", hint: "Create, edit, and launch ads" },
  reporting: { label: "Reporting", hint: "Performance charts and spend" },
  tracking: { label: "Tracking", hint: "Pixels and conversion events" },
  identity: { label: "Pages", hint: "Pages and creative uploads" },
  billing: { label: "Billing", hint: "View billing status and cards" },
};

function TeamView({ role }: { role: "owner" | "admin" | "member" }) {
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setError("");
    try { setTeam(await api<TeamResponse>("/api/ads/team")); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "The team could not be loaded."); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const invite = async () => {
    const email = inviteEmail.trim();
    if (!email.includes("@")) { setError("Enter a valid email address."); return; }
    setBusy("invite");
    setError("");
    try {
      await api("/api/ads/team", { method: "POST", body: JSON.stringify({ email, role: inviteRole }) });
      setInviteEmail("");
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The invitation could not be sent."); }
    finally { setBusy(""); }
  };
  const patchMember = async (member: TeamMember, patch: { role?: "admin" | "member"; permissions?: string[] }) => {
    setBusy(member.id);
    setError("");
    try {
      await api(`/api/ads/team/${encodeURIComponent(member.id)}`, { method: "PATCH", body: JSON.stringify(patch) });
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The team member could not be updated."); }
    finally { setBusy(""); }
  };
  const resend = async (member: TeamMember) => {
    setBusy(member.id);
    setError("");
    setNotice("");
    try {
      const result = await api<{ message: string }>(`/api/ads/team/${encodeURIComponent(member.id)}/resend`, { method: "POST" });
      setNotice(result.message || "Invitation email resent.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The invitation could not be resent."); }
    finally { setBusy(""); }
  };
  const remove = async (member: TeamMember) => {
    if (!window.confirm(`Remove ${member.email} from this workspace?`)) return;
    setBusy(member.id);
    setError("");
    try {
      await api(`/api/ads/team/${encodeURIComponent(member.id)}`, { method: "DELETE" });
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The team member could not be removed."); }
    finally { setBusy(""); }
  };

  return <section className="team-workspace">
    <div className="team-invite">
      <div><strong>Invite a teammate</strong><span>They get a branded email and sign in with a six-digit code. Access applies to this workspace only.</span></div>
      <div className="team-invite__form">
        <input value={inviteEmail} type="email" placeholder="teammate@company.com" onChange={(event) => setInviteEmail(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void invite(); }} />
        <select value={inviteRole} onChange={(event) => setInviteRole(event.target.value === "admin" ? "admin" : "member")}>
          <option value="member">Member</option>
          {role === "owner" && <option value="admin">Admin</option>}
        </select>
        <button className="button button--primary button--small" type="button" disabled={busy === "invite"} onClick={() => void invite()}>{busy === "invite" ? "Inviting…" : "Send invite"}</button>
      </div>
    </div>
    {error && <p className="team-error" role="alert">{error}</p>}
    {notice && <p className="team-notice" role="status">{notice}</p>}
    {!team && !error && <div className="team-loading">Loading the team…</div>}
    {team && team.members.length === 0 && <div className="team-empty"><Users /><strong>No teammates yet</strong><p>Invite people to work in this workspace with the exact access you choose.</p></div>}
    {team && team.members.length > 0 && <div className="team-list">
      {team.members.map((member) => {
        const memberBusy = busy === member.id;
        const locked = role === "admin" && member.role === "admin";
        return <article key={member.id}>
          <div className="team-list__top">
            <div className="team-list__who"><strong>{member.email}</strong><span>{member.status === "invited" ? <>Invited, waiting for first sign-in <button className="team-list__resend" type="button" disabled={memberBusy || locked} onClick={() => void resend(member)}>{memberBusy ? "Sending…" : "Resend invite"}</button></> : "Active"}</span></div>
            <select value={member.role} disabled={memberBusy || locked || role !== "owner"} onChange={(event) => void patchMember(member, { role: event.target.value === "admin" ? "admin" : "member" })}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button className="team-list__remove" type="button" disabled={memberBusy || locked} onClick={() => void remove(member)} aria-label={`Remove ${member.email}`}><Trash2 /></button>
          </div>
          {member.role === "admin"
            ? <p className="team-list__all"><Check /> Full access: admins can use every part of the workspace.</p>
            : <div className="team-list__perms" role="group" aria-label={`Access for ${member.email}`}>
              {team.available_permissions.map((permission) => {
                const meta = PERMISSION_META[permission] || { label: permission, hint: "" };
                const active = member.permissions.includes(permission);
                return <button key={permission} type="button" className={`perm-toggle ${active ? "is-on" : ""}`} aria-pressed={active} disabled={memberBusy || locked} onClick={() => void patchMember(member, { permissions: active ? member.permissions.filter((candidate) => candidate !== permission) : [...member.permissions, permission] })}>
                  <span className="perm-toggle__box" aria-hidden="true">{active && <Check />}</span>
                  <span className="perm-toggle__text"><strong>{meta.label}</strong><small>{meta.hint}</small></span>
                </button>;
              })}
            </div>}
        </article>;
      })}
    </div>}
    <p className="team-footnote">Adding a new payment card stays with the account owner: cards are tied to the owner’s verified identity.</p>
  </section>;
}
